import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchCollections, sendTestEmail } from '../api/client';

/**
 * Group wings by collectionNumber for the dropdown.
 */
function groupByCollection(wings) {
  const groups = new Map();
  for (const wing of wings) {
    const num = wing.collectionNumber || 0;
    if (!groups.has(num)) {
      groups.set(num, {
        collectionNumber: num,
        collectionName: wing.collectionName || wing.name,
        wings: [],
      });
    }
    groups.get(num).wings.push(wing);
  }
  return Array.from(groups.values())
    .sort((a, b) => a.collectionNumber - b.collectionNumber)
    .map(g => ({ ...g, wings: g.wings.sort((a, b) => a.collectionId.localeCompare(b.collectionId)) }));
}

/**
 * Extract the email template from a wing's steps array.
 */
function extractEmail(wing) {
  for (const step of wing.steps || []) {
    if (step.type === 'email') {
      return { templateId: step.templateId, stepNumber: step.stepNumber, name: step.name };
    }
  }
  return null;
}

export default function Testing() {
  const { user, getAccessTokenSilently } = useAuth0();
  const [wings, setWings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWingId, setSelectedWingId] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [sendState, setSendState] = useState(null);

  const loadWings = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const result = await fetchCollections(token);
      setWings(result.collections || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { loadWings(); }, [loadWings]);

  const selectedWing = wings.find(w => w.collectionId === selectedWingId);
  const emailStep = selectedWing ? extractEmail(selectedWing) : null;
  const groups = groupByCollection(wings);

  const handleSendTest = async () => {
    if (!emailStep || !email) return;
    setSendState({ status: 'sending' });

    try {
      const token = await getAccessTokenSilently();
      const payload = {
        templateId: emailStep.templateId,
        email,
        sequenceId: selectedWing.sequenceId || selectedWing.collectionId,
      };
      if (firstName) payload.firstName = firstName;
      if (lastName) payload.lastName = lastName;
      if (companyName) payload.companyName = companyName;

      await sendTestEmail(token, payload);
      setSendState({ status: 'success', message: 'Test email sent' });
    } catch (err) {
      setSendState({ status: 'error', message: err.message });
    }

    setTimeout(() => setSendState(null), 5000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-text-secondary">Loading wings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-extrabold text-text-primary">Test Emails</h2>

      {error && (
        <div className="bg-alert/10 border-2 border-alert rounded-xl p-4 shadow-brutal">
          <p className="text-alert text-sm font-semibold">{error}</p>
        </div>
      )}

      {/* Configuration */}
      <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal p-6 space-y-4">
        <h3 className="text-lg font-bold text-text-primary">Configuration</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-text-secondary mb-1">
              Recipient Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-text-secondary mb-1">
              Wing
            </label>
            <select
              value={selectedWingId}
              onChange={(e) => setSelectedWingId(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            >
              <option value="">Select a wing...</option>
              {groups.map((group) => (
                <optgroup key={group.collectionNumber} label={`${group.collectionNumber}. ${group.collectionName}`}>
                  {group.wings.map((wing) => (
                    <option key={wing.collectionId} value={wing.collectionId}>
                      {wing.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-text-secondary mb-1">
              First Name (optional)
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              placeholder="Jane"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-text-secondary mb-1">
              Last Name (optional)
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              placeholder="Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-text-secondary mb-1">
              Company Name (optional)
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              placeholder="Acme Inc."
            />
          </div>
        </div>
      </div>

      {/* Selected wing details + send */}
      {!selectedWingId ? (
        <div className="text-center py-12">
          <p className="text-text-secondary">Select a wing above to send a test email.</p>
        </div>
      ) : selectedWing && emailStep ? (
        <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal p-6 space-y-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/20 text-primary text-sm font-bold border border-primary/30">
              {selectedWing.collectionNumber}
            </span>
            <div>
              <h3 className="text-lg font-bold text-text-primary">{selectedWing.name}</h3>
              <p className="text-xs text-text-secondary">{selectedWing.collectionName}</p>
            </div>
            <span className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
              selectedWing.status === 'active'
                ? 'bg-success/15 text-success border border-success/30'
                : 'bg-surface text-text-secondary border border-ink/20'
            }`}>
              {selectedWing.status}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-text-secondary">Trigger:</span>
              <span className="ml-2 font-mono text-text-primary bg-background px-2 py-0.5 rounded border border-ink/10">
                {selectedWing.triggerEvent}
              </span>
            </div>
            <div>
              <span className="text-text-secondary">Template:</span>
              <span className="ml-2 font-mono text-text-primary bg-background px-2 py-0.5 rounded border border-ink/10">
                {emailStep.templateId}
              </span>
            </div>
          </div>

          {selectedWing.description && (
            <p className="text-sm text-text-secondary">{selectedWing.description}</p>
          )}

          <div className="flex items-center gap-4 pt-2">
            <button
              onClick={handleSendTest}
              disabled={!email || sendState?.status === 'sending'}
              className="px-5 py-2 bg-primary text-white text-sm font-semibold rounded-xl border-2 border-ink shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg active:translate-y-0.5 active:shadow-brutal-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendState?.status === 'sending' ? 'Sending...' : 'Send Test Email'}
            </button>

            {sendState?.status === 'success' && (
              <span className="text-sm text-success font-semibold">{sendState.message}</span>
            )}
            {sendState?.status === 'error' && (
              <span className="text-sm text-alert font-semibold">{sendState.message}</span>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-text-secondary">This wing has no email template.</p>
        </div>
      )}
    </div>
  );
}
